import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../store/authSlice";
import { clearLicenseExpired } from "../../store/licenseSlice";

const MainLayout = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isBusinessRoute = location.pathname.startsWith("/businesses");

  // Get user from Redux to check if super admin
  const user = useSelector((state) => state.auth.user);
  const licenseState = useSelector((state) => state.license);
  const isSuperAdmin = user?.isPlatformAdmin === true;
  const isLicenseExpired = licenseState?.isExpired === true;
  const licenseMessage =
    licenseState?.message || "License expired. Please renew to continue.";
  const renewalBusinessId =
    licenseState?.businessId || user?.businessId || null;
  const renewalPath = isSuperAdmin
    ? renewalBusinessId
      ? `/businesses/${renewalBusinessId}/licenses`
      : "/businesses"
    : null;

  const handleLogout = () => {
    dispatch(logout());
    dispatch(clearLicenseExpired());
    navigate("/login");
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const getLinkClass = (path, disabled = false) => {
    const baseClass =
      "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition";
    const activeClass = "bg-slate-200 text-slate-900 font-bold shadow-md";
    const inactiveClass = "text-slate-700 hover:bg-slate-100";
    const disabledClass = disabled
      ? "pointer-events-none cursor-not-allowed opacity-50"
      : "";
    return `${baseClass} ${isActive(path) ? activeClass : inactiveClass} ${disabledClass}`;
  };

  const mainContentRef = useRef(null);

  useEffect(() => {
    mainContentRef.current?.scrollTo({ top: 0, behavior: "auto" });
  }, [location.pathname, location.search]);

  return (
    <div className="flex h-screen overflow-hidden bg-[var(--color-sand)] text-[var(--color-ink)]">
      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-full border-r border-[rgba(15,23,42,0.08)] bg-white transition-all duration-300 ${
          isSidebarOpen ? "w-64" : "w-0"
        } overflow-hidden`}
      >
        <div className="flex h-full flex-col">
          {/* Sidebar Header */}
          <div className="border-b border-[rgba(15,23,42,0.08)] px-6 py-3.5">
            <span className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">
              Admin Portal
            </span>
          </div>

          {/* Sidebar Navigation */}
          <nav className="flex-1 space-y-3 overflow-y-auto px-3 py-3.5">
            {/* Businesses Section - Only for Super Admin */}
            {isSuperAdmin && isBusinessRoute && (
              <div className="mb-4 space-y-2">
                <div className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Businesses
                </div>
                <Link to="/businesses" className={getLinkClass("/businesses")}>
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                  All Businesses
                </Link>

                <Link
                  to="/businesses/new"
                  className={getLinkClass("/businesses/new")}
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  New Business
                </Link>
              </div>
            )}

            {/* Menu Section - Only for Non-Super Admin */}
            {!isSuperAdmin && (
              <div className="mb-4 space-y-2">
                <div className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Menu
                </div>
                <Link
                  to="/menu/categories"
                  className={getLinkClass("/menu/categories")}
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 10h16M4 14h16M4 18h16"
                    />
                  </svg>
                  Categories
                </Link>

                <Link to="/menu/items" className={getLinkClass("/menu/items")}>
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                  Items
                </Link>

                <Link
                  to="/menu/modifiers"
                  className={getLinkClass("/menu/modifiers")}
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
                    />
                  </svg>
                  Add Ons
                </Link>

                {/* <Link
                  to="/menu/bundles"
                  className={getLinkClass("/menu/bundles")}
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                    />
                  </svg>
                  Bundles
                </Link> */}

                {/* <Link
                  to="/menu/promotions"
                  className={getLinkClass("/menu/promotions")}
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                    />
                  </svg>
                  Promotions
                </Link> */}

                <Link
                  to="/menu/upsell-campaigns"
                  className={getLinkClass("/menu/upsell-campaigns")}
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8V6m0 10v2m9-6a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Upsell Campaigns
                </Link>

                {/* <Link
                  to="/menu/day-overrides"
                  className={getLinkClass("/menu/day-overrides")}
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10m-11 9h12a2 2 0 002-2V7a2 2 0 00-2-2H6a2 2 0 00-2 2v11a2 2 0 002 2z"
                    />
                  </svg>
                  Day Overrides
                </Link> */}

                <Link
                  to="/menu/weekly-scheduling"
                  className={getLinkClass("/menu/weekly-scheduling")}
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10m-11 9h12a2 2 0 002-2V7a2 2 0 00-2-2H6a2 2 0 00-2 2v11a2 2 0 002 2z"
                    />
                  </svg>
                  Weekly Scheduling
                </Link>
              </div>
            )}

            {/* Menu Attributes Section - Only for Non-Super Admin */}
            {!isSuperAdmin && (
              <div className="mb-4 space-y-2">
                <div className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Menu Attributes
                </div>
                {/* <Link to="/menu/tags" className={getLinkClass("/menu/tags")}>
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                    />
                  </svg>
                  Tags
                </Link> */}

                <Link
                  to="/menu/allergens"
                  className={getLinkClass("/menu/allergens")}
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  Allergens
                </Link>

                <Link
                  to="/menu/protein-types"
                  className={getLinkClass("/menu/protein-types")}
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
                    />
                  </svg>
                  Protein Types
                </Link>
              </div>
            )}

            {/* User Management Section - Show to Everyone */}
            {/* <div className="space-y-2">
              <div className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
                User Management
              </div>
              <Link to="/users" className={getLinkClass("/users")}>
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                  />
                </svg>
                Users
              </Link>

              <Link to="/roles" className={getLinkClass("/roles")}>
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
                Roles
              </Link>

              <Link to="/permissions" className={getLinkClass("/permissions")}>
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
                Permissions
              </Link>
            </div> */}

            {!isSuperAdmin && (
              <div className="mt-4 space-y-2">
                <div className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Reporting
                </div>
                <Link
                  to="/reporting/completed-orders"
                  className={
                    location.pathname.startsWith("/reporting/completed-orders")
                      ? "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-bold transition bg-slate-200 text-slate-900 shadow-md"
                      : getLinkClass("/reporting/completed-orders")
                  }
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 17v-6m4 6V7m4 10v-3M5 21h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2z"
                    />
                  </svg>
                  Completed Orders
                </Link>

                <Link
                  to="/reporting/top-items"
                  className={getLinkClass("/reporting/top-items")}
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h10M4 18h7"
                    />
                  </svg>
                  Items
                </Link>
              </div>
            )}

            {/* Settings Section - Only for Non-Super Admin */}
            {!isSuperAdmin && (
              <div className="mt-4 space-y-2">
                <div className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Settings
                </div>
                <Link
                  to="/settings/general"
                  className={getLinkClass(
                    "/settings/general",
                    isLicenseExpired,
                  )}
                  aria-disabled={isLicenseExpired}
                  title={
                    isLicenseExpired
                      ? "License renewal required to access settings."
                      : undefined
                  }
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  General
                </Link>
                <Link
                  to="/settings/order-types"
                  className={getLinkClass(
                    "/settings/order-types",
                    isLicenseExpired,
                  )}
                  aria-disabled={isLicenseExpired}
                  title={
                    isLicenseExpired
                      ? "License renewal required to access settings."
                      : undefined
                  }
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                  Order Types
                </Link>

                {/* <Link
                  to="/settings/qr-config"
                  className={getLinkClass(
                    "/settings/qr-config",
                    isLicenseExpired,
                  )}
                  aria-disabled={isLicenseExpired}
                  title={
                    isLicenseExpired
                      ? "License renewal required to access settings."
                      : undefined
                  }
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"
                    />
                  </svg>
                  QR Configuration
                </Link> */}

                {/* <Link
                  to="/menu/preview"
                  className={getLinkClass("/menu/preview")}
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                  Menu Preview
                </Link> */}
              </div>
            )}
            {!isSuperAdmin && isLicenseExpired && (
              <p className="px-3 text-xs font-medium text-rose-600">
                License renewal required to access Settings.
              </p>
            )}
          </nav>

          {/* Sidebar Footer */}
          <div className="border-t border-[rgba(15,23,42,0.08)] px-3 py-4">
            <button
              type="button"
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
              onClick={handleLogout}
            >
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              Log out
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div
        className={`flex h-screen flex-1 flex-col overflow-hidden transition-all duration-300 ${
          isSidebarOpen ? "ml-64" : "ml-0"
        }`}
      >
        {/* Top Bar */}
        <div className="sticky top-0 z-30 border-b border-[rgba(15,23,42,0.08)] bg-white/70 backdrop-blur">
          <div className="flex items-center justify-between px-6 py-2">
            <button
              type="button"
              onClick={toggleSidebar}
              className="rounded-lg p-2 text-slate-600 transition hover:bg-slate-100"
              aria-label="Toggle sidebar"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>

            {/* Store selector removed per request */}
          </div>
        </div>

        {isLicenseExpired && (
          <div className="border-b border-rose-200 bg-rose-50 px-6 py-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm font-medium text-rose-700">
                {licenseMessage}
              </p>
              {renewalPath ? (
                <Link
                  to={renewalPath}
                  className="rounded-lg border border-rose-300 bg-white px-3 py-1.5 text-sm font-semibold text-rose-700 transition hover:bg-rose-100"
                >
                  Renew License
                </Link>
              ) : (
                <p className="text-xs font-medium text-rose-700">
                  Contact your platform admin to renew the license.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Page Content */}
        <div
          ref={mainContentRef}
          id="main-content-scroll"
          className="flex-1 overflow-y-auto px-6 py-10"
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default MainLayout;
